export type PageHealthBand = 'critical' | 'weak' | 'ok' | 'best' | 'unscored';
export type PageHealthModel = 'realization_gap' | 'eligibility' | 'unscored';
export type RealizationGapGate = 'scored' | 'low_confidence' | 'pending' | 'unscored';

export interface PageRealizationCeilingDriverSnapshot {
  keyword: string;
  searchVolume: number;
  matchingTokens: string[];
  upsideAtTargetRank: number;
}

export interface PageRealizationSnapshot {
  realizedVisibility: number;
  realizedSource: 'etv' | 'traffic_proxy';
  demandCeiling: number | null;
  ceiling: number;
  realizationRatio: number | null;
  gap: number | null;
  gapGate: RealizationGapGate;
  ceilingDriver: PageRealizationCeilingDriverSnapshot | null;
  alignedKeywordCount: number;
}

export interface PageHealthClassification {
  band: PageHealthBand;
  reasons: string[];
  model: PageHealthModel;
}

export type SecondaryHealthModel = 'none' | 'freshness_hold' | 'earned_visibility';

export interface SecondaryPageHealthSnapshot {
  band: PageHealthBand | null;
  model: SecondaryHealthModel;
  reasons: string[];
  recentlyUpdated: boolean;
  unscoredSegment:
    | 'no_claim'
    | 'low_confidence_claim'
    | 'no_market_data'
    | 'no_aligned_ceiling'
    | 'immaterial_ceiling'
    | null;
}

export type EffectiveHealthDisplayBand = PageHealthBand | 'recently_updated';

export type InventoryHealthFilterBand = EffectiveHealthDisplayBand;

export interface PageHealthInput {
  statusCode: number;
  estimatedOrganicTraffic: number | null;
  realization?: PageRealizationSnapshot | null;
  pageHealth?: PageHealthClassification | null;
}

const HEALTH_WEAK_REALIZATION_RATIO_MAX = 0.5;
const HEALTH_WEAK_GAP_MIN = 20;
const HEALTH_BEST_REALIZATION_RATIO_MIN = 0.85;
const HEALTH_BEST_REALIZATION_RATIO_MAX = 1;
const HEALTH_BEST_REALIZED_TRAFFIC_MIN = 30;

function gapGateReason(gapGate: RealizationGapGate | undefined): string {
  if (gapGate === 'unscored') return 'No demand claim';
  if (gapGate === 'low_confidence') return 'Low-confidence demand claim';
  if (gapGate === 'pending') return 'Gap unscored (awaiting earned market data or demand ceiling)';
  return 'Realization gap scored';
}

export function classifyPageHealth(input: PageHealthInput): PageHealthClassification {
  if (input.pageHealth) {
    return input.pageHealth;
  }

  const statusCode = Number.isFinite(input.statusCode) ? input.statusCode : 0;
  if (statusCode >= 400) {
    return {
      band: 'critical',
      model: 'eligibility',
      reasons: [`HTTP status ${statusCode}`]
    };
  }

  const realization = input.realization;
  if (realization?.gapGate === 'scored' &&
    realization.realizationRatio !== null &&
    realization.gap !== null) {
    const ratio = realization.realizationRatio;
    const gap = realization.gap;
    const realized = realization.realizedVisibility;

    if (ratio >= HEALTH_BEST_REALIZATION_RATIO_MIN &&
      ratio <= HEALTH_BEST_REALIZATION_RATIO_MAX &&
      realized >= HEALTH_BEST_REALIZED_TRAFFIC_MIN) {
      return {
        band: 'best',
        model: 'realization_gap',
        reasons: [
          `Realization ratio ${ratio.toFixed(2)}`,
          `Est. traffic ${realized.toFixed(1)}`,
          `Gap ${gap.toFixed(1)}`
        ]
      };
    }

    if (ratio < HEALTH_WEAK_REALIZATION_RATIO_MAX && gap >= HEALTH_WEAK_GAP_MIN) {
      return {
        band: 'weak',
        model: 'realization_gap',
        reasons: [
          `Realization ratio ${ratio.toFixed(2)}`,
          `Gap ${gap.toFixed(1)}`,
          `Est. traffic ${realized.toFixed(1)}`
        ]
      };
    }

    return {
      band: 'ok',
      model: 'realization_gap',
      reasons: [
        `Realization ratio ${ratio.toFixed(2)}`,
        `Gap ${gap.toFixed(1)}`,
        `Est. traffic ${realized.toFixed(1)}`
      ]
    };
  }

  return {
    band: 'unscored',
    model: 'unscored',
    reasons: [gapGateReason(realization?.gapGate)]
  };
}

export function pageHealthBandLabel(band: PageHealthBand): string {
  if (band === 'critical') return 'Critical';
  if (band === 'weak') return 'Weak';
  if (band === 'best') return 'Best';
  if (band === 'unscored') return 'Unscored';
  return 'OK';
}

export function inventoryHealthFilterLabel(band: InventoryHealthFilterBand): string {
  if (band === 'recently_updated') return 'Recently updated';
  return pageHealthBandLabel(band);
}

export function pageHealthBandClass(band: PageHealthBand): string {
  if (band === 'critical') return 'score-low';
  if (band === 'weak') return 'score-mid';
  if (band === 'best') return 'score-high';
  if (band === 'unscored') return 'score-neutral';
  return 'score-high';
}

export function healthBandSortRank(band: PageHealthBand): number {
  if (band === 'best') return 4;
  if (band === 'ok') return 3;
  if (band === 'weak') return 2;
  if (band === 'critical') return 1;
  return 0;
}

export function effectiveHealthSortRank(band: EffectiveHealthDisplayBand): number {
  if (band === 'best') return 5;
  if (band === 'ok') return 4;
  if (band === 'weak') return 3;
  if (band === 'recently_updated') return 2;
  if (band === 'critical') return 1;
  return 0;
}

export function secondaryHealthSortRank(
  secondary: SecondaryPageHealthSnapshot | null | undefined
): number {
  if (!secondary) return -1;
  if (secondary.band) return healthBandSortRank(secondary.band);
  if (secondary.model === 'freshness_hold') return effectiveHealthSortRank('recently_updated');
  return -1;
}

export function pageHealthSortRank(input: PageHealthInput): number {
  return healthBandSortRank(classifyPageHealth(input).band);
}

export function secondaryHealthBandLabel(
  secondary: SecondaryPageHealthSnapshot | null | undefined
): string {
  if (!secondary) return '—';
  if (secondary.band) return pageHealthBandLabel(secondary.band);
  if (secondary.model === 'freshness_hold') return 'Recently updated';
  return '—';
}

export function secondaryHealthBandClass(
  secondary: SecondaryPageHealthSnapshot | null | undefined
): string {
  if (!secondary) return 'score-neutral';
  if (secondary.band) return pageHealthBandClass(secondary.band);
  if (secondary.model === 'freshness_hold') return 'score-neutral';
  return 'score-neutral';
}

export function resolveEffectiveHealthBand(
  primaryBand: PageHealthBand,
  secondary: SecondaryPageHealthSnapshot | null | undefined
): EffectiveHealthDisplayBand {
  if (primaryBand !== 'unscored') return primaryBand;
  if (secondary?.band) return secondary.band;
  if (secondary?.model === 'freshness_hold') return 'recently_updated';
  return 'unscored';
}

export function effectiveHealthBandLabel(
  primaryBand: PageHealthBand,
  secondary: SecondaryPageHealthSnapshot | null | undefined
): string {
  const band = resolveEffectiveHealthBand(primaryBand, secondary);
  if (band === 'recently_updated') return 'Recently updated';
  return pageHealthBandLabel(band);
}

export function effectiveHealthBandClass(
  primaryBand: PageHealthBand,
  secondary: SecondaryPageHealthSnapshot | null | undefined
): string {
  const band = resolveEffectiveHealthBand(primaryBand, secondary);
  if (band === 'recently_updated') return 'score-neutral';
  return pageHealthBandClass(band);
}
